/*
 * Utilidades de la página para responder (y de la vista individual de respuestas, que pinta
 * el formulario ya contestado con los mismos campos). Lo que ya existe en lib/formularios.js
 * (secciones, validación, tema) NO se repite aquí.
 */
import { INICIO, barajar, esPregunta, secciones } from '../../../lib/formularios';

/* ============================== Semilla de barajado ============================== */

/**
 * Semilla por visita: el orden aleatorio no puede cambiar en cada render (las preguntas
 * saltarían mientras la persona escribe), pero sí entre una visita y otra.
 */
export function semillaDeVisita(token) {
  const clave = `orvian_form_semilla:${token || 'vista'}`;
  try {
    let s = sessionStorage.getItem(clave);
    if (!s) {
      s = Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem(clave, s);
    }
    return s;
  } catch {
    return 'orvian';
  }
}

/** Items de una sección en el orden que toca (barajados si el formulario lo pide). */
export function itemsEnOrden(seccion, ajustes, semilla) {
  const items = seccion?.items || [];
  return ajustes?.orden_aleatorio ? barajar(items, `${semilla}:${seccion.id}`) : items;
}

/* ============================== Precompletar ============================== */

const buscarOpcion = (item, valor) => {
  const v = String(valor || '').trim();
  if (!v) return null;
  return (item.opciones || []).find((o) => o.id === v)
    || (item.opciones || []).find((o) => (o.texto || '').trim().toLowerCase() === v.toLowerCase())
    || null;
};

const RE_FECHA = /^(\d{4}|--)-\d{2}-\d{2}(T\d{2}:\d{2})?$/;
const RE_HORA = /^\d{2}:\d{2}(:\d{2})?$/;

/**
 * Respuestas que trae la URL (`?e.<item_id>=valor`), como en los enlaces precompletados de
 * Google. En elección vale el id de la opción o su texto; lo que no casa con nada va a "Otro"
 * si la pregunta lo tiene. Cuadrículas y archivos no se precompletan.
 */
export function leerPrecompletado(items, search) {
  const params = new URLSearchParams(search || '');
  const porId = Object.fromEntries((items || []).map((it) => [it.id, it]));
  const out = {};
  params.forEach((valor, clave) => {
    if (!clave.startsWith('e.')) return;
    const item = porId[clave.slice(2)];
    if (!item || !esPregunta(item)) return;
    switch (item.tipo) {
      case 'texto_corto':
      case 'parrafo':
        if (valor) out[item.id] = valor;
        break;
      case 'opcion_multiple':
      case 'desplegable': {
        const op = buscarOpcion(item, valor);
        if (op) out[item.id] = { opcion: op.id };
        else if (valor && item.otra && item.tipo === 'opcion_multiple') out[item.id] = { otro: valor };
        break;
      }
      case 'casillas': {
        const ids = [];
        let otro = null;
        valor.split(',').forEach((parte) => {
          const op = buscarOpcion(item, parte);
          if (op && !ids.includes(op.id)) ids.push(op.id);
          else if (!op && parte.trim() && item.otra) otro = parte.trim();
        });
        if (ids.length || otro) out[item.id] = { opciones: ids, otro };
        break;
      }
      case 'escala': {
        const n = parseInt(valor, 10);
        const { min = 1, max = 5 } = item.escala || {};
        if (!Number.isNaN(n) && n >= min && n <= max) out[item.id] = n;
        break;
      }
      case 'calificacion': {
        const n = parseInt(valor, 10);
        if (!Number.isNaN(n) && n >= 1 && n <= (item.calificacion?.max || 5)) out[item.id] = n;
        break;
      }
      case 'fecha':
        if (RE_FECHA.test(valor)) out[item.id] = valor;
        break;
      case 'hora':
        if (RE_HORA.test(valor)) out[item.id] = valor;
        break;
      default:
        break;
    }
  });
  return out;
}

/** ¿La URL trae algún `e.`? (entonces no se restaura el borrador encima sin más) */
export const traePrecompletado = (search) => [...new URLSearchParams(search || '').keys()].some((k) => k.startsWith('e.'));

/** Enlace precompletado con las respuestas actuales (modo "Obtener enlace" de la vista previa). */
export function enlacePrecompletado(base, token, items, respuestas) {
  const params = new URLSearchParams();
  (items || []).forEach((item) => {
    const v = respuestas?.[item.id];
    if (v === null || v === undefined || v === '') return;
    switch (item.tipo) {
      case 'opcion_multiple':
      case 'desplegable':
        if (v.opcion) params.append(`e.${item.id}`, v.opcion);
        else if (v.otro) params.append(`e.${item.id}`, v.otro);
        break;
      case 'casillas': {
        const partes = [...(v.opciones || [])];
        if (v.otro) partes.push(v.otro);
        if (partes.length) params.append(`e.${item.id}`, partes.join(','));
        break;
      }
      case 'texto_corto':
      case 'parrafo':
      case 'escala':
      case 'calificacion':
      case 'fecha':
      case 'hora':
        if (String(v).trim()) params.append(`e.${item.id}`, String(v));
        break;
      default:
        break; // cuadrículas y archivos no se pueden precompletar
    }
  });
  const q = params.toString();
  return `${base}/f/${token}${q ? `?${q}` : ''}`;
}

/* ============================== Recorrido y respuestas ============================== */

/** Solo las respuestas de las secciones que la persona recorrió (el servidor descarta el resto igual). */
export function respuestasDeRecorrido(items, respuestas, visitadas) {
  const lista = secciones(items);
  const out = {};
  lista.filter((s) => visitadas.includes(s.id)).forEach((s) => {
    s.items.forEach((it) => {
      const v = respuestas?.[it.id];
      if (v !== undefined && v !== null) out[it.id] = v;
    });
  });
  return out;
}

/** Id de la sección que contiene una pregunta (para llevar a la persona al primer error del servidor). */
export function seccionDe(items, itemId) {
  const s = secciones(items).find((sec) => sec.items.some((it) => it.id === itemId));
  return s ? s.id : INICIO;
}

/* ============================== Otros ============================== */

export const RE_CORREO = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function nuevaOperacion() {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

/** Mensaje legible de un `detail` del backend (texto u objeto `{mensaje}`). */
export function mensajeDe(err, defecto) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d.mensaje === 'string') return d.mensaje;
  return defecto;
}

export function fechaLarga(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}
